import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { InputFile } from "components/InputFIle/InputFile";
import { Loader } from "components/Loader/Loader";
import { useFormik } from "formik";
import { DiseñoCurricularI } from "interfaces/DiseñoCurricular";
import { useEffect, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { createDiseñosCurricularesSchema } from "schemas/diseñosCurriculares/createDiseñosCurricularesSchema";
import {
  postDiseñoCurricular,
  putDiseñoCurricular,
  getUrlPrefirmadaDC,
  postDiseñoCurricularV2,
  putDiseñoCurricularV2,
} from "services/diseñoCurricular";
import { uploadFileToPresignedUrl } from "services/request/s3PutFile";
import {
  createDisenioCurricularBatch,
  editDisenioCurricularBatch,
  BatchUploadError,
} from "services/batchUpload";
import { getDocumentacionFile } from "services/normativas";
import { formatDate } from "utils/formatUtils";
import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import { getNiveles } from "services/niveles";
import { NivelI } from "interfaces/Nivel";
import { EstadoDiseñoCurricularI } from "interfaces/EstadoDiseñoCurricular";
import { getEstados } from "services/general";
import { InputMultiFile } from "components/InputFIle/InputMultiFile";
import { EntityID } from "api/types/EntityID";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  elemento?: DiseñoCurricularI;
  onSuccess?: (newDiseñoCurricular: DiseñoCurricularI) => void;
  showModal: boolean;
}

type FakeFile = File & {
  fakeId: EntityID;
  isVirtual: boolean;
  nombre_real?: string;
}

interface initialValues {
  id?: string | number;
  nombre: string;
  descripcion: string;
  fecha_publicacion: string;
  estado_id: string | number;
  documentacion_adjunta?: FakeFile[];
  nivel_id: number | string;
}

interface defaultOptionsSelected {
  default_selected_nivel: NivelI | null;
  default_selected_estado_id: EstadoDiseñoCurricularI | null;
}

type DisenioCurricularAdjuntos = Partial<Record<"documentacion_adjunta", File[]>>;

export const DiseñosCurricularesABM = ({
  actionType,
  onClose,
  elemento,
  onSuccess,
  showModal,
}: Props) => {
  const isEditMode = actionType == "edit";
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const initialValues: initialValues = {
    nombre: "",
    descripcion: "",
    fecha_publicacion: "",
    estado_id: "",
    documentacion_adjunta: [],
    nivel_id: "",
  };

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_selected_nivel: null,
      default_selected_estado_id: null,
    });

  const renderInitialValues = async () => {
    if (isEditMode && elemento) {
      // Seteamos los valores básicos inmediatamente para mostrar el form rápido
      const currentValues = {
        id: elemento?.id ?? "",
        nombre: elemento?.nombre ?? "",
        descripcion: elemento?.descripcion ?? "",
        fecha_publicacion: elemento?.fechaPublicacion
          ? formatDate.formDate(elemento.fechaPublicacion)
          : "",
        estado_id: elemento?.estado.id ?? "",
        documentacion_adjunta: [],
        nivel_id: elemento.nivel.id,
      };
      setDefaultOptionsSelected({
        default_selected_nivel: {
          id: elemento.nivel.id,
          nombre: elemento.nivel.nombre,
        },
        default_selected_estado_id: {
          id: elemento.estado.id,
          nombre: elemento.estado.nombre,
          textColor: elemento.estado.textColor,
          backgroundColor: elemento.estado.backgroundColor,
          borderColor: elemento.estado.borderColor,
        },
      });
      setValues(currentValues);
    // Bajamos la llamada de los archivos para que no interfieran con set inicial
      setIsLoadingFiles(true);
      try {
        const adjuntos: FakeFile[] = [];

        for (const adjunto of elemento.documentacionAdjunta) {
          if (adjunto) {
            const adjuntoFile = await getDocumentacionFile({
              id: adjunto.id,
              fileName: adjunto.nombre,
            }) as FakeFile;

            adjuntoFile.fakeId = adjunto.id;
            adjuntoFile.isVirtual = true;
            adjuntoFile.nombre_real = adjunto.nombre_real || adjunto.nombre;

            adjuntos.push(adjuntoFile);
          }
        }

        setValues((prev) => ({ ...prev, documentacion_adjunta: adjuntos }));
      } finally {
        setIsLoadingFiles(false);
      }
    } else {
      setValues(initialValues);
    }
  };

  const handleSubmitCreateUrlPrefirmada = async (params: initialValues) => {
    try {
      const newParams = { ...params };

      if (newParams.fecha_publicacion) {
        newParams.fecha_publicacion = new Date(
          `${newParams.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }

      const archivosParaSubir: FakeFile[] = params.documentacion_adjunta ?? [];

      // PASO 1: Obtener URLs prefirmadas
      const urlsPrefirmadas = await getUrlPrefirmadaDC({
        documentos: archivosParaSubir.map((f) => ({
          nombre: f.name,
          mimetype: f.type,
          size: f.size,
          type: 'documentacion_adjunta',
        })),
        requestPath: '/disenio-curricular',
      });

      // PASO 2: Subir todos los archivos a S3
      await Promise.all(
        urlsPrefirmadas.map((urlInfo, i) =>
          uploadFileToPresignedUrl(urlInfo.url, archivosParaSubir[i])
        )
      );

      // PASO 3: Confirmar en el backend con los keys de S3
      const documentacion_adjunta = urlsPrefirmadas.map((urlInfo, i) => ({
        key: urlInfo.key,
        nombre: urlInfo.key,
        nombre_real: archivosParaSubir[i].name,
        mime_type: archivosParaSubir[i].type,
        size: archivosParaSubir[i].size,
      }));

      delete (newParams as any).documentacion_adjunta;

      const newDiseñoCurricular = await postDiseñoCurricularV2({
        ...newParams,
        documentacion_adjunta,
      } as any);

      showSuccessToast("¡El diseño curricular fue creado con éxito!");
      if (onSuccess) onSuccess(newDiseñoCurricular);
      onClose();
    } catch (err: any) {
      showErrorToast("El diseño curricular no pudo ser creado.");
      console.log(err.message);
    }
  };

  const handleSubmitEditUrlPrefirmada = async (params: initialValues) => {
    try {
      const newParams = { ...params };

      if (newParams.fecha_publicacion) {
        newParams.fecha_publicacion = new Date(
          `${newParams.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }

      // Calcular qué documentos existentes se eliminaron
      // isVirtual son los archivos que ya están en el servidor.
      const activesVirtuales = params.documentacion_adjunta?.filter((f) => f.isVirtual) ?? [];

      const adjuntosAEliminarIds = (elemento?.documentacionAdjunta ?? [])
        .filter((orig) => !activesVirtuales.find((av) => av.fakeId === orig.id))
        .map((orig) => orig.id);

      // Separar archivos nuevos (reales) de los existentes (virtuales)
      // Solo los nuevos pasan por el flujo de la prefirma de s3.
      const nuevosArchivos = params.documentacion_adjunta?.filter((f) => !f.isVirtual) ?? [];

      let documentacion_adjunta_keys: { key: string; nombre: string; nombre_real: string; mime_type: string; size: number }[] = [];

      if (nuevosArchivos.length > 0) {
        // PASO 1: Obtener URLs prefirmadas (solo para archivos nuevos)
        const urlsPrefirmadas = await getUrlPrefirmadaDC({
          documentos: nuevosArchivos.map((f) => ({
            nombre: f.name,
            mimetype: f.type,
            size: f.size,
            type: 'documentacion_adjunta',
          })),
          requestPath: '/disenio-curricular',
        });

        // PASO 2: Subir todos los nuevos archivos a S3
        await Promise.all(
          urlsPrefirmadas.map((urlInfo, i) =>
            uploadFileToPresignedUrl(urlInfo.url, nuevosArchivos[i])
          )
        );

        documentacion_adjunta_keys = urlsPrefirmadas.map((urlInfo, i) => ({
          key: urlInfo.key,
          nombre: urlInfo.key,
          nombre_real: nuevosArchivos[i].name,
          mime_type: nuevosArchivos[i].type,
          size: nuevosArchivos[i].size,
        }));
      }

      // PASO 3: Confirmar en el backend con keys nuevas + ids a eliminar
      delete (newParams as any).documentacion_adjunta;

      const newDiseñoCurricular = await putDiseñoCurricularV2({
        ...newParams,
        id: elemento!.id,
        documentacion_eliminar: adjuntosAEliminarIds,
        documentacion_adjunta: documentacion_adjunta_keys,
      } as any);

      showSuccessToast("¡La resolución fue editada con éxito!");
      if (onSuccess) onSuccess(newDiseñoCurricular);
      onClose();
    } catch (err: any) {
      showErrorToast("La resolución no pudo ser editada.");
      console.log(err.message);
    }
  };

  const handleSubmitCreate = async (params: initialValues) => {
    try {
      const newParams = { ...params };
      const allDocs = [...(params.documentacion_adjunta ?? [])];
      delete newParams.documentacion_adjunta;

      if (newParams.fecha_publicacion) {
        newParams.fecha_publicacion = new Date(
          `${newParams.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }

      const newDiseñoCurricular = await createDisenioCurricularBatch({
        formParams: newParams,
        documentos: allDocs,
      });

      showSuccessToast("¡El diseño curricular fue creado con éxito!");
      if (onSuccess) onSuccess(newDiseñoCurricular);
      onClose();
    } catch (err: any) {
      if (err instanceof BatchUploadError) {
        const archivos = err.failedBatches.flatMap((b) => b.fileNames).join(", ");
        showErrorToast(
          `El diseño fue creado pero falló la subida de algunos documentos: ${archivos}`
        );
      } else {
        showErrorToast("El diseño curricular no pudo ser creado.");
      }
      console.log(err.message);
    }
  };

  const handleSubmitEdit = async (params: initialValues) => {
    try {
      const newParams = { ...params };

      if (newParams.fecha_publicacion) {
        newParams.fecha_publicacion = new Date(
          `${newParams.fecha_publicacion}T00:00:00`,
        ).toISOString();
      }

      // Separar virtuales de nuevos
      const newDocs: File[] = [];
      const activeImages: FakeFile[] = [];

      newParams.documentacion_adjunta?.forEach((doc) => {
        if (!doc.isVirtual) newDocs.push(doc);
        else activeImages.push(doc);
      });
      delete newParams.documentacion_adjunta;

      // Calcular eliminaciones
      const documentacionEliminar: EntityID[] = [];
      elemento?.documentacionAdjunta.forEach((oa) => {
        if (!activeImages.find((aa) => aa.fakeId == oa.id)) {
          documentacionEliminar.push(oa.id);
        }
      });

      const newDiseñoCurricular = await editDisenioCurricularBatch({
        formParams: newParams,
        disenoId: elemento!.id,
        nuevosDocumentos: newDocs,
        documentacionEliminar,
      });

      showSuccessToast("¡La resolución fue editada con éxito!");
      if (onSuccess) onSuccess(newDiseñoCurricular);
      onClose();
    } catch (err: any) {
      if (err instanceof BatchUploadError) {
        const archivos = err.failedBatches.flatMap((b) => b.fileNames).join(", ");
        showErrorToast(
          `La resolución fue editada pero falló la subida de algunos documentos: ${archivos}`
        );
      } else {
        showErrorToast("La resolución no pudo ser editada.");
      }
      console.log(err.message);
    }
  };


  const handleChangeFile = (e: any) => {
    handleChange(e);
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createDiseñosCurricularesSchema,
    onSubmit: actionType == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  useEffect(() => {
    setIsLoadingFiles(false);
    resetForm();
    if (actionType) {
      renderInitialValues();
    }
  }, [actionType]);

  return (
    <>
      <Modal
        show={showModal}
        onHide={onClose}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>
            {isEditMode
              ? "Editar diseño curricular"
              : "Nuevo diseño curricular"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body style={{ position: "relative" }}>
            {isLoadingFiles && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.93)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  gap: "12px",
                  borderRadius: "4px",
                }}
              >
                <Loader style={{ margin: "0" }} />
                <span style={{ color: "#555", fontSize: "0.9rem" }}>
                  Cargando...
                </span>
              </div>
            )}
            <Col>
              <Row>
                <Col sm={6} className="mb-4">
                  <Input
                    id={"nombre"}
                    title="Nombre del diseño curricular"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6} className="mb-4">
                  <Input
                    id={"descripcion"}
                    title="Descripcion"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6} className="mb-4">
                  <Input
                    id={"fecha_publicacion"}
                    title="Fecha de publicación"
                    required
                    type={"date"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6} className="mb-4">
                  <InputMultiFile
                    id={"documentacion_adjunta"}
                    title="Cargar documento oficial"
                    required
                    onChange={handleChangeFile}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={6} className="mb-4">
                  <AsyncSelect
                    onChange={handleChange}
                    id="nivel_id"
                    values={values}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    placeholder={"Buscar nombre del nivel"}
                    size="lg"
                    title="Nivel"
                    getter={getNiveles}
                    paramKey={"nombre"}
                    variant="gray-container"
                    arrElementsPath="elementos"
                    optionsFormatConfig={{
                      value: "id",
                      label: (el: NivelI) => `${el.nombre}`,
                    }}
                    defaultSelectedOptions={
                      defaultOptionsSelected["default_selected_nivel"]
                    }
                    required
                    noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                    emptySelectMessage={`Ingresar un nombre`}
                  />
                </Col>

                <Col sm={6} className="mb-4">
                  <AsyncSelect
                    onChange={handleChange}
                    id="estado_id"
                    values={values}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    placeholder={"Buscar nombre del estado"}
                    size="lg"
                    title="Estado"
                    getter={getEstados}
                    paramKey={"nombre"}
                    variant="gray-container"
                    arrElementsPath="elementos"
                    optionsFormatConfig={{
                      value: "id",
                      label: (el: EstadoDiseñoCurricularI) => `${el.nombre}`,
                    }}
                    defaultSelectedOptions={
                      defaultOptionsSelected["default_selected_estado_id"]
                    }
                    required
                    noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                    emptySelectMessage={`Ingresar un nombre`}
                  />
                </Col>
              </Row>
            </Col>
          </Modal.Body>
          {!isLoadingFiles && (
            <Modal.Footer>
              <Button
                className="mx-3"
                variant="secondary"
                size="lg"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="user-submit-button"
                disabled={isSubmitting}
                type="submit"
              >
                {isEditMode ? "Guardar Cambios" : "Confirmar"}
              </Button>
            </Modal.Footer>
          )}
        </form>
      </Modal>
    </>
  );
};
